import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import ProfileDropdown from "../Components/Common/ProfileDropdown";
import logo from "../assets/images/colosseum/ColosseumLogo.png";
import { useAuthStore } from "../store/useAuthStore";

const menuItems = [
  { label: "Homepage", href: "/dashboard", navigable: true },
  { label: "Battles", href: "/battles", navigable: true },
  { label: "My Bets", href: "/bets/history", navigable: true },
  { label: "Leaderboard", href: "/leaderboard", navigable: true },
  { label: "Profile", href: "/profile", navigable: true },
];

const formatPoints = (points?: number) => {
  return new Intl.NumberFormat("fr-FR").format(points ?? 0);
};

const Sidebar = () => {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMobileMenuOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isMobileMenuOpen]);

  return (
    <header className={`colosseum-user-nav${isMobileMenuOpen ? " is-menu-open" : ""}`}>
      <div className="colosseum-user-nav__inner">
        <button
          type="button"
          className="colosseum-user-nav__menu-toggle"
          aria-expanded={isMobileMenuOpen}
          aria-controls="colosseum-user-navigation"
          aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
          data-cy="mobile-menu-toggle"
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>

        <Link to="/dashboard" className="colosseum-user-nav__brand" aria-label="Go to dashboard">
          <img src={logo} alt="" aria-hidden="true" />
        </Link>

        <nav
          id="colosseum-user-navigation"
          className="colosseum-user-nav__scroll"
          aria-label="User navigation"
        >
          <div className="colosseum-user-nav__mobile-header">
            <Link
              to="/dashboard"
              className="colosseum-user-nav__mobile-brand"
              aria-label="Go to dashboard"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <img src={logo} alt="" aria-hidden="true" />
            </Link>
          </div>
          <ul className="colosseum-user-nav__list">
            {menuItems.map((item) => {
              const active = item.navigable && location.pathname === item.href;
              const className = `colosseum-user-nav__link${active ? " is-active" : ""}`;

              return (
                <li className="colosseum-user-nav__item" key={item.label}>
                  {item.navigable ? (
                    <Link
                      to={item.href}
                      className={className}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button type="button" className={className}>
                      {item.label}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="colosseum-user-nav__account" aria-label="Player account summary">
          <div className="colosseum-user-nav__balance" data-cy="sidebar-balance">
            <span className="colosseum-user-nav__balance-dot" aria-hidden="true" />
            <strong>{formatPoints(user?.pointsBalance)}</strong>
            <span>PTS</span>
          </div>
          <ProfileDropdown variant="avatar" />
        </div>
      </div>
      <button
        type="button"
        className="colosseum-user-nav__backdrop"
        aria-label="Close menu backdrop"
        onClick={() => setIsMobileMenuOpen(false)}
        tabIndex={isMobileMenuOpen ? 0 : -1}
      />
      <div className="vertical-overlay" />
    </header>
  );
};

export default Sidebar;
